import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../../../shared/api/apiClient";
import CrmJourneyTimeline from "../../components/CrmJourneyTimeline";
import TaskPhotosGallery from "./TaskPhotosGallery";
import { getUserPermissions } from "../../../../../src/shared/lib/auth/authHelper";
import "./TasksPage.css";

const formatBackendError = (raw, fallback, t) => {
  if (!raw) return fallback;
  const status = raw?.response?.status;
  const detail =
    raw?.response?.data?.detail ?? raw?.response?.data?.message ?? null;

  if (status === 429) {
    const retryAfter =
      raw?.response?.data?.retry_after_seconds ??
      raw?.response?.headers?.["retry-after"];
    const seconds = Number(retryAfter);

    return Number.isFinite(seconds) && seconds > 0
      ? t("rate_limit_retry_seconds", { seconds: Math.ceil(seconds) })
      : t("rate_limit_retry_later");
  }

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        typeof item === "string" ? item : item?.msg || item?.message || "",
      )
      .filter(Boolean);
    return messages.length ? messages.join("; ") : fallback;
  }

  if (typeof detail === "object") {
    return detail?.msg || detail?.message || fallback;
  }

  if (raw?.code === "ERR_NETWORK" || raw?.message === "Network Error") {
    return t("network_connection_failed");
  }

  return typeof raw?.message === "string" && raw.message
    ? raw.message
    : fallback;
};

const parseBackendLocalDateTimeParts = (value) => {
  if (!value) return null;

  const raw = String(value).trim();

  // Backend stores due_at as tenant-local naive datetime.
  // Do not pass it through new Date(), otherwise browser timezone may shift it.
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!match) return null;

  return {
    year: match[1],
    month: match[2],
    day: match[3],
    hour: match[4],
    minute: match[5],
    second: match[6] || "00",
  };
};

const formatTenantLocalDateTime = (value) => {
  const parts = parseBackendLocalDateTimeParts(value);
  if (!parts) return value ? String(value) : "-";

  return `${parts.day}.${parts.month}.${parts.year} ${parts.hour}:${parts.minute}`;
};

const tenantLocalDateTimeSortValue = (value) => {
  const parts = parseBackendLocalDateTimeParts(value);
  if (!parts) return value ? Number.MAX_SAFE_INTEGER : null;

  return Number(
    `${parts.year}${parts.month}${parts.day}${parts.hour}${parts.minute}${parts.second}`,
  );
};

const applySorting = (items, config) => {
  if (!config || !config.key) return items;

  const { key, direction } = config;
  const dir = direction === "asc" ? 1 : -1;

  const getVal = (task) => {
    if (!task) return null;

    switch (key) {
      case "id":
      case "ui_id":
      case "owner_ui_id":
      case "lead_id":
      case "lead_ui_id":
      case "deal_id":
      case "contact_ui_id":
        return task[key] != null ? Number(task[key]) : null;
      case "due_at":
        return tenantLocalDateTimeSortValue(task[key]);
      case "created_at":
        return task[key] ? new Date(task[key]).getTime() : null;
      default:
        return task[key] ?? null;
    }
  };

  const sorted = [...items].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  return sorted;
};

const TASK_TIME_TABS = [
  { key: "overdue", label: "tasks_tab_overdue" },
  { key: "today", label: "tasks_tab_today" },
  { key: "upcoming", label: "tasks_tab_upcoming" },
  { key: "completed", label: "tasks_tab_completed" },
];

const getBrowserLocalDateKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getBrowserLocalDateTime = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 19);
};

const defaultColumnFilters = {
  ui_id: "",
  title: "",
  description: "",
  due_at: "",
  status: "",
  owner_ui_id: "",
  lead_ui_id: "",
  deal_id: "",
  contact_ui_id: "",
  created_at: "",
};

const getCompactPaginationItems = (currentPage, totalPages) => {
  const pages = [];
  const add = (item) => {
    if (!pages.includes(item)) pages.push(item);
  };

  if (!totalPages || totalPages <= 1) return [1];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) add(i);
    return pages;
  }

  add(1);

  if (currentPage > 4) add("left-ellipsis");

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i += 1) add(i);

  if (currentPage < totalPages - 3) add("right-ellipsis");

  add(totalPages);
  return pages;
};

const normalizeDateTimeLocalForBackend = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;

  // datetime-local returns "YYYY-MM-DDTHH:mm".
  // Do NOT use new Date(...).toISOString() here, because it converts local time to UTC
  // and shifts tenant/user selected time, for example 11:49 -> 08:49.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) {
    return `${raw}:00`;
  }

  // Accept already normalized value.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
    return raw;
  }

  return raw;
};

const TasksPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const academyParams = new URLSearchParams(location.search);
  const academyOverdueActive =
    academyParams.get("academy_overdue") === "1";
  const academyTableTaskActive =
    academyParams.get("academy_table_task") === "1";
  const academyTaskId = academyParams.get("academy_task_id") || "";
  const academyRouteRef = useRef(location.search);
  const initialNavigationStateRef = useRef(location.state || {});
  const isAcademyCreateFlow = Boolean(
    initialNavigationStateRef.current.academy_return_to &&
      initialNavigationStateRef.current.lead_id,
  );

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [openMenuTaskId, setOpenMenuTaskId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const openMenuRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({
    key: "ui_id",
    direction: "desc",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage, setTasksPerPage] = useState(10);

  const [activeTaskTab, setActiveTaskTab] = useState("overdue");
  const [taskTabCounts, setTaskTabCounts] = useState({
    overdue: 0,
    today: 0,
    upcoming: 0,
    completed: 0,
  });
  const [taskTabCache, setTaskTabCache] = useState({
    overdue: null,
    today: null,
    upcoming: null,
    completed: null,
  });

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState(null);
  const [savingFilter, setSavingFilter] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [descriptionFullscreen, setDescriptionFullscreen] = useState(false);
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [descriptionError, setDescriptionError] = useState("");
  const [detailsTimeline, setDetailsTimeline] = useState([]);
  const [detailsTimelineLoading, setDetailsTimelineLoading] = useState(false);
  const [pendingOpenTaskId, setPendingOpenTaskId] = useState(null);

  const [createContacts, setCreateContacts] = useState([]);
  const [createUIContacts, setCreateUIContacts] = useState([]);
  const [createLeadId, setCreateLeadId] = useState(null);
  const [createDealId, setCreateDealId] = useState(null);
  const [createLeadUiId, setCreateLeadUiId] = useState(null);
  const [createReturnTo, setCreateReturnTo] = useState("");

  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    due_at: "",
  });
  const [createError, setCreateError] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const createSubmitBusyRef = useRef(false);

  const isAcademyTrainingTask = (task) => {
    if (!academyTaskId) return false;
    const expected = String(academyTaskId);
    return String(task?.id ?? "") === expected ||
      String(task?.ui_id ?? "") === expected;
  };

  const notifyTaskDataChanged = () => {
    window.dispatchEvent(new Event("velsoft:tasks-changed"));
    window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
  };

  const fetchTasks = async (requestedTab = activeTaskTab, options = {}) => {
    const tab = ["overdue", "today", "upcoming", "completed"].includes(
      requestedTab,
    )
      ? requestedTab
      : activeTaskTab;
    const force = Boolean(options?.force);
    const initial = Boolean(options?.initial);

    if (!force && Array.isArray(taskTabCache[tab])) {
      setActiveTaskTab(tab);
      setTasks(taskTabCache[tab]);
      setCurrentPage(1);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const loadGroup = async (group) => {
        const response = await api.get("/tasks/grouped", {
          params: {
            group,
            today: getBrowserLocalDateKey(),
            now: getBrowserLocalDateTime(),
            skip: 0,
            limit: 1000,
          },
        });

        return {
          items: Array.isArray(response.data?.items) ? response.data.items : [],
          counts: response.data?.counts || {},
        };
      };

      const first = await loadGroup(tab);
      const normalizedCounts = {
        overdue: Number(first.counts.overdue || 0),
        today: Number(first.counts.today || 0),
        upcoming: Number(first.counts.upcoming || 0),
        completed: Number(first.counts.completed || 0),
      };

      setTaskTabCounts(normalizedCounts);
      setTaskTabCache((prev) => ({
        ...prev,
        [tab]: first.items,
      }));

      let finalTab = tab;
      let finalItems = first.items;

      if (initial && tab === "overdue" && normalizedCounts.overdue === 0) {
        finalTab = "today";
        const todayResult = await loadGroup("today");
        finalItems = todayResult.items;
        setTaskTabCache((prev) => ({
          ...prev,
          today: todayResult.items,
        }));
      }

      setActiveTaskTab(finalTab);
      setTasks(finalItems);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      setError(formatBackendError(e, t("error_fetching_tasks"), t));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskTabChange = async (tab) => {
    if (tab === activeTaskTab) return;

    setOpenMenuTaskId(null);
    setCurrentPage(1);
    setActiveTaskTab(tab);

    if (Array.isArray(taskTabCache[tab])) {
      setTasks(taskTabCache[tab]);
      return;
    }

    await fetchTasks(tab);
  };

  const invalidateTaskTabs = () => {
    setTaskTabCache({
      overdue: null,
      today: null,
      upcoming: null,
      completed: null,
    });
  };

  const applySavedFilter = (filter) => {
    if (!filter) return;

    const filtersObj = filter.filters || {};
    const cf = filtersObj.columnFilters || defaultColumnFilters;
    const sq = filtersObj.searchQuery || "";

    setSearchQuery(sq);
    setColumnFilters({
      ...defaultColumnFilters,
      ...cf,
    });

    setSortConfig({
      key: filter.sort_by || "ui_id",
      direction: filter.sort_dir || "desc",
    });

    setTasksPerPage(filter.per_page || 10);
    setCurrentPage(1);
    setSelectedFilterId(filter.id);
  };

  const loadSavedFilters = async () => {
    try {
      setFiltersLoading(true);
      setFiltersError(null);

      const res = await api.get("/saved-filters/tasks");
      const items = res.data || [];
      setSavedFilters(items);

      const def = items.find((f) => f.is_default);
      if (def && !selectedFilterId) {
        applySavedFilter(def);
      }
    } catch (e) {
      console.error(e);
      setFiltersError(
        e?.response?.data?.detail || t("error_loading_saved_filters"),
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleSelectSavedFilter = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedFilterId(null);
      return;
    }

    const id = Number(val);
    const f = savedFilters.find((x) => x.id === id);
    if (f) {
      applySavedFilter(f);
    }
  };

  const handleSaveCurrentAsNew = async () => {
    const name = window.prompt(t("enter_filter_name")) || "";
    if (!name.trim()) return;

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: name.trim(),
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: tasksPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.post("/saved-filters/tasks", payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(e?.response?.data?.detail || t("error_saving_filter"));
    } finally {
      setSavingFilter(false);
    }
  };

  const handleUpdateSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const makeDefault = window.confirm(t("make_filter_default"));

    const payload = {
      name: undefined,
      filters: {
        searchQuery,
        columnFilters,
      },
      sort_by: sortConfig.key,
      sort_dir: sortConfig.direction,
      per_page: tasksPerPage,
      is_default: makeDefault,
    };

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.put(`/saved-filters/${selectedFilterId}`, payload);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(e?.response?.data?.detail || t("error_updating_filter"));
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(t("select_filter_first"));
      return;
    }

    const confirmed = window.confirm(t("confirm_delete_filter"));
    if (!confirmed) return;

    try {
      setSavingFilter(true);
      setFiltersError(null);

      await api.delete(`/saved-filters/${selectedFilterId}`);
      setSelectedFilterId(null);
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(e?.response?.data?.detail || t("error_deleting_filter"));
    } finally {
      setSavingFilter(false);
    }
  };

  useEffect(() => {
    if (isAcademyCreateFlow) return;
    fetchTasks("overdue", {
      force: true,
      initial: !academyOverdueActive,
    });
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (academyRouteRef.current === location.search) return;
    academyRouteRef.current = location.search;

    if (academyOverdueActive) {
      fetchTasks("overdue", { force: true });
    } else if (academyTableTaskActive) {
      fetchTasks("upcoming", { force: true });
    }
    // Academy changes query parameters without remounting TasksPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (openMenuTaskId == null) return;

    const onDown = (e) => {
      if (!openMenuRef.current) return;
      if (!openMenuRef.current.contains(e.target)) {
        setOpenMenuTaskId(null);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenuTaskId(null);
    };

    const onScroll = () => {
      setOpenMenuTaskId(null);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [openMenuTaskId]);

  const openActionsMenu = (taskId, event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();

    const menuWidth = 240;
    const menuHeight = 178;

    const left = Math.max(
      8,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
    );

    const top =
      rect.bottom + menuHeight > window.innerHeight
        ? Math.max(8, rect.top - menuHeight - 6)
        : rect.bottom + 6;

    setMenuPosition({ top, left });
    setOpenMenuTaskId((prev) => (prev === taskId ? null : taskId));
  };

  useEffect(() => {
    const state = initialNavigationStateRef.current;
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    );

    const contactIds = Array.isArray(state.contact_ids)
      ? state.contact_ids
      : [];

    const contactUiIds = Array.isArray(state.contact_ui_ids)
      ? state.contact_ui_ids
      : [];

    const leadId = state.lead_id ?? null;
    const dealId = state.deal_id ?? null;
    const leadUiId = state.lead_ui_id ?? null;
    const defaultTitle = state.default_title || "";
    const defaultDescription = state.default_description || "";
    const defaultDueAt = state.default_due_at || "";
    const academyReturnTo = state.academy_return_to || "";
    const openTaskId = state.open_task_id ?? state.task_id ?? null;
    const taskSearch =
      state.task_search ?? (openTaskId ? String(openTaskId) : "");

    if (openTaskId) {
      setPendingOpenTaskId(openTaskId);
      setSearchQuery(taskSearch);
      setColumnFilters((prev) => ({ ...prev, ui_id: String(openTaskId) }));
      return;
    }

    if (contactIds.length > 0 || leadId || dealId) {
      setCreateContacts(contactIds);
      setCreateUIContacts(contactUiIds);
      setCreateLeadId(leadId);
      setCreateDealId(dealId);
      setCreateLeadUiId(leadUiId);
      setCreateReturnTo(academyReturnTo);
      setCreateForm({
        title: defaultTitle,
        description: defaultDescription,
        due_at: defaultDueAt,
      });
      setCreateError("");
      setCreateModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingOpenTaskId || tasks.length === 0) return;

    const target = tasks.find((task) => {
      const needle = String(pendingOpenTaskId);
      return String(task.id) === needle || String(task.ui_id) === needle;
    });

    if (!target) return;

    setDetailsTask(target);
    setCurrentPage(1);
    setPendingOpenTaskId(null);
  }, [pendingOpenTaskId, tasks]);

  const updateTaskStatus = async (task, newStatus) => {
    if (updatingId !== null) return;
    if (task.status === newStatus) return;

    const confirmed = window.confirm(
      t("confirm_change_task_status", {
        id: task.id,
        title: task.title,
        status: newStatus,
      }),
    );
    if (!confirmed) return;

    try {
      setOpenMenuTaskId(null);
      setUpdatingId(task.id);
      setError(null);

      await api.put(`/tasks/${task.id}`, {
        status: newStatus,
      });

      setTasks((prev) =>
        prev.map((item) =>
          item.id === task.id ? { ...item, status: newStatus } : item,
        ),
      );
      notifyTaskDataChanged();
      invalidateTaskTabs();
      await fetchTasks(activeTaskTab, { force: true });
    } catch (e) {
      console.error(e);
      setError(formatBackendError(e, t("error_updating_task"), t));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteTask = async (task) => {
    if (deletingId !== null) return;

    const confirmed = window.confirm(
      t("confirm_delete_task", {
        id: task.ui_id ?? task.id,
        title: task.title,
        defaultValue: "Delete task #{{id}}?",
      }),
    );

    if (!confirmed) return;

    try {
      setOpenMenuTaskId(null);
      setDeletingId(task.id);
      setError(null);

      await api.delete(`/tasks/${task.id}`);

      setTasks((prev) => prev.filter((item) => item.id !== task.id));
      notifyTaskDataChanged();
      invalidateTaskTabs();
      await fetchTasks(activeTaskTab, { force: true });
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("error_deleting_task", { defaultValue: "Failed to delete task" }),
          t,
        ),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "done":
        return "badge bg-success";
      case "overdue":
        return "badge bg-danger";
      case "pending":
      default:
        return "badge bg-warning text-dark";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "done":
        return t("task_status_done");
      case "overdue":
        return t("task_status_overdue");
      case "pending":
      default:
        return t("task_status_pending");
    }
  };

  const isDoneDisabled = (task) =>
    updatingId === task.id || task.status === "done";

  const isBackToPendingDisabled = (task) =>
    updatingId === task.id || task.status === "pending";

  const closeCreateModal = () => {
    if (createSubmitBusyRef.current) return;
    setCreateModalOpen(false);
    setCreateError("");
    setCreateSaving(false);

    if (createReturnTo) {
      navigate(createReturnTo, { replace: true });
    }
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateSubmit = async () => {
    if (createSubmitBusyRef.current) return;

    const title = createForm.title.trim();
    const description = createForm.description.trim();
    const dueAtRaw = createForm.due_at.trim();

    if (!title) {
      setCreateError(t("task_title_required"));
      return;
    }

    let due_at = null;
    if (dueAtRaw) {
      due_at = normalizeDateTimeLocalForBackend(dueAtRaw);
    }

    const requests = [];

    if (createContacts.length > 0) {
      createContacts.forEach((contactId) => {
        requests.push(
          api.post("/tasks", {
            title,
            description: description || null,
            due_at,
            contact_id: contactId,
            lead_id: createLeadId,
            deal_id: createDealId,
          }),
        );
      });
    } else if (createLeadId || createDealId) {
      requests.push(
        api.post("/tasks", {
          title,
          description: description || null,
          due_at,
          lead_id: createLeadId,
          deal_id: createDealId,
        }),
      );
    } else {
      setCreateError(t("task_no_related_entity"));
      return;
    }

    try {
      createSubmitBusyRef.current = true;
      setCreateSaving(true);
      setCreateError("");

      await Promise.all(requests);

      setCreateModalOpen(false);
      setCreateError("");
      notifyTaskDataChanged();
      if (createReturnTo) {
        navigate(createReturnTo, { replace: true });
        return;
      }
      invalidateTaskTabs();
      await fetchTasks(activeTaskTab, { force: true });
    } catch (e) {
      console.error(e);
      setCreateError(formatBackendError(e, t("error_creating_tasks"), t));
    } finally {
      createSubmitBusyRef.current = false;
      setCreateSaving(false);
    }
  };

  const normalizeValue = (v) =>
    v === null || v === undefined ? "" : String(v).toLowerCase();

  const formatDateForFilter = (value, { tenantLocal = false } = {}) => {
    if (!value) return "";

    if (tenantLocal) {
      return formatTenantLocalDateTime(value).toLowerCase();
    }

    try {
      return new Date(value).toLocaleString().toLowerCase();
    } catch {
      return String(value).toLowerCase();
    }
  };

  const entityButtonStyle = {
    border: "none",
    background: "transparent",
    color: "#8fb3ff",
    padding: 0,
    fontWeight: 750,
    textAlign: "left",
    cursor: "pointer",
    lineHeight: 1.16,
    display: "block",
    maxWidth: 220,
    minWidth: 0,
  };

  const entityTitleStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const entityMetaStyle = {
    display: "block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgba(255,255,255,0.52)",
    fontSize: 11,
    marginTop: 2,
    fontWeight: 600,
  };

  const renderOwnerCell = (task) => {
    if (!task?.owner_id) return "-";
    const label =
      task.owner_display_name ||
      task.owner_email ||
      `${t("sales_tasks_owner", { defaultValue: "Owner" })} #${task.owner_ui_id || task.owner_id}`;
    const meta =
      task.owner_email || (task.owner_ui_id ? `ID ${task.owner_ui_id}` : "");
    return (
      <button
        type="button"
        style={entityButtonStyle}
        onClick={() => navigate(`/menu/employees?user_id=${task.owner_id}`)}
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const renderContactCell = (task) => {
    if (!task?.contact_id) return "-";
    const label =
      task.contact_display_name ||
      task.contact_primary_phone ||
      task.contact_primary_email ||
      `${t("sales_tasks_contact", { defaultValue: "Contact" })} #${task.contact_ui_id || task.contact_id}`;
    const meta =
      task.contact_primary_phone ||
      task.contact_primary_email ||
      (task.contact_ui_id ? `ID ${task.contact_ui_id}` : "");
    return (
      <button
        type="button"
        style={entityButtonStyle}
        onClick={() =>
          navigate(`/menu/sales/contacts?contact_id=${task.contact_id}`)
        }
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const renderLeadCell = (task) => {
    if (!task?.lead_id) return "-";
    const label =
      task.lead_title ||
      `${t("sales_tasks_lead", { defaultValue: "Lead" })} #${task.lead_ui_id || task.lead_id}`;
    const meta = task.lead_ui_id ? `ID ${task.lead_ui_id}` : "";
    return (
      <button
        type="button"
        style={entityButtonStyle}
        onClick={() => navigate(`/menu/sales/leads?lead_id=${task.lead_id}`)}
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const renderDealCell = (task) => {
    if (!task?.deal_id && !task?.deal_ui_id) return "-";

    const dealUiId = task.deal_ui_id ?? null;
    const dealInternalId = task.deal_id ?? null;
    const visibleId = dealUiId ?? dealInternalId;
    const label =
      task.deal_title || `${t("deal", { defaultValue: "Deal" })} #${visibleId}`;
    const meta = dealUiId ? `ID ${dealUiId}` : "";

    const goToDeal = () => {
      if (dealUiId) {
        navigate(
          `/menu/sales/deals?deal_ui_id=${encodeURIComponent(dealUiId)}`,
        );
        return;
      }
      navigate(
        `/menu/sales/deals?deal_id=${encodeURIComponent(dealInternalId || "")}`,
      );
    };

    return (
      <button
        type="button"
        style={entityButtonStyle}
        onClick={goToDeal}
        title={dealUiId ? `Open deal #${dealUiId}` : "Open deal"}
      >
        <span style={entityTitleStyle}>{label}</span>
        {meta && <span style={entityMetaStyle}>{meta}</span>}
      </button>
    );
  };

  const getTimelineIcon = (item) => {
    const subject = String(item?.subject || "").toLowerCase();
    if (subject.includes("convert")) return "🎯";
    if (subject.includes("task")) return "📋";
    if (subject.includes("telegram")) return "🤖";
    if (subject.includes("csv") || subject.includes("import")) return "📥";
    if (subject.includes("email")) return "✉️";
    if (subject.includes("budget")) return "💰";
    if (subject.includes("prepayment") || subject.includes("предоплат"))
      return "💳";
    if (item?.type === "call") return "☎️";
    if (item?.type === "message") return "💬";
    return "•";
  };

  const getTimelineTitle = (item) =>
    item?.subject || item?.content || item?.type || "Activity";

  const fetchTaskTimeline = async (task) => {
    if (!task?.ui_id && !task?.id) {
      setDetailsTimeline([]);
      return;
    }
    try {
      setDetailsTimelineLoading(true);
      const taskUiId = task.ui_id || task.id;
      const res = await api.get(`/activities/timeline/task/${taskUiId}`);
      setDetailsTimeline(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch task timeline", err);
      setDetailsTimeline([]);
    } finally {
      setDetailsTimelineLoading(false);
    }
  };

  const filteredAndSortedTasks = useMemo(() => {
    let result = [...tasks];

    const global = searchQuery.trim().toLowerCase();

    if (global) {
      result = result.filter((task) => {
        const fields = [
          task.id,
          task.ui_id,
          task.title,
          task.description,
          task.status,
          task.owner_ui_id,
          task.owner_display_name,
          task.owner_email,
          task.lead_id,
          task.lead_ui_id,
          task.lead_title,
          task.deal_id,
          task.deal_ui_id,
          task.deal_title,
          task.contact_ui_id,
          task.contact_display_name,
          task.contact_primary_email,
          task.contact_primary_phone,
          formatDateForFilter(task.due_at, { tenantLocal: true }),
          formatDateForFilter(task.created_at),
        ];
        return fields.some((field) => normalizeValue(field).includes(global));
      });
    }

    result = result.filter((task) => {
      const cf = columnFilters;

      if (cf.ui_id && !String(task.ui_id ?? "").includes(cf.ui_id.trim()))
        return false;
      if (
        cf.title &&
        !normalizeValue(task.title).includes(cf.title.trim().toLowerCase())
      )
        return false;
      if (
        cf.description &&
        !normalizeValue(task.description).includes(
          cf.description.trim().toLowerCase(),
        )
      )
        return false;
      if (
        cf.status &&
        !normalizeValue(task.status).includes(cf.status.trim().toLowerCase())
      )
        return false;
      if (cf.owner_ui_id) {
        const needle = cf.owner_ui_id.trim().toLowerCase();
        const haystack = [
          task.owner_ui_id,
          task.owner_display_name,
          task.owner_email,
        ]
          .map((v) => normalizeValue(v))
          .join(" ");
        if (!haystack.includes(needle)) return false;
      }
      if (cf.lead_ui_id) {
        const needle = cf.lead_ui_id.trim().toLowerCase();
        const haystack = [task.lead_ui_id, task.lead_id, task.lead_title]
          .map((v) => normalizeValue(v))
          .join(" ");
        if (!haystack.includes(needle)) return false;
      }
      if (cf.deal_id) {
        const needle = cf.deal_id.trim().toLowerCase();
        const haystack = [task.deal_ui_id, task.deal_id, task.deal_title]
          .map((v) => normalizeValue(v))
          .join(" ");
        if (!haystack.includes(needle)) return false;
      }
      if (cf.contact_ui_id) {
        const needle = cf.contact_ui_id.trim().toLowerCase();
        const haystack = [
          task.contact_ui_id,
          task.contact_display_name,
          task.contact_primary_email,
          task.contact_primary_phone,
        ]
          .map((v) => normalizeValue(v))
          .join(" ");
        if (!haystack.includes(needle)) return false;
      }
      if (
        cf.due_at &&
        !formatDateForFilter(task.due_at, { tenantLocal: true }).includes(
          cf.due_at.trim().toLowerCase(),
        )
      )
        return false;
      if (
        cf.created_at &&
        !formatDateForFilter(task.created_at).includes(
          cf.created_at.trim().toLowerCase(),
        )
      )
        return false;

      return true;
    });

    result = applySorting(result, sortConfig);

    return result;
  }, [tasks, searchQuery, columnFilters, sortConfig]);

  const totalTasks = filteredAndSortedTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalTasks / (tasksPerPage || 1)));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * tasksPerPage;
  const visibleTasks = filteredAndSortedTasks.slice(
    startIdx,
    startIdx + tasksPerPage,
  );

  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleTasksPerPageChange = (e) => {
    const value = Number(e.target.value);
    if (!value || value <= 0) return;
    setTasksPerPage(value);
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      let direction = "asc";
      if (prev.key === key && prev.direction === "asc") {
        direction = "desc";
      }
      return { key, direction };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const showingText =
    totalTasks === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", {
          start: startIdx + 1,
          end: Math.min(startIdx + tasksPerPage, totalTasks),
          total: totalTasks,
        });

  const handleRefreshClick = async () => {
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setSortConfig({ key: "ui_id", direction: "desc" });
    setTasksPerPage(10);
    setCurrentPage(1);
    setSelectedFilterId(null);
    setFiltersError(null);
    setOpenMenuTaskId(null);
    invalidateTaskTabs();
    await fetchTasks(activeTaskTab, { force: true });
  };

  const openLinkedContact = (task) => {
    if (!task?.contact_id) return;

    setDetailsTask(null);
    setOpenMenuTaskId(null);

    navigate(`/menu/sales/contacts?contact_id=${task.contact_id}`);
  };

  const openLinkedLead = (task) => {
    if (!task?.lead_id) return;

    setDetailsTask(null);
    setOpenMenuTaskId(null);

    navigate(`/menu/sales/leads?lead_id=${task.lead_id}`);
  };

  const openLinkedDeal = (task) => {
    if (!task?.deal_id) return;

    setDetailsTask(null);
    setOpenMenuTaskId(null);

    navigate(`/menu/sales/deals?deal_id=${task.deal_id}`);
  };

  const completeTaskAndOpenLead = async (task) => {
    if (!task?.lead_id || updatingId !== null) return;

    try {
      setDetailsTask(null);
      setOpenMenuTaskId(null);
      setUpdatingId(task.id);
      setError(null);

      if (task.status !== "done") {
        await api.put(`/tasks/${task.id}`, {
          status: "done",
        });

        setTasks((prev) =>
          prev.map((item) =>
            item.id === task.id ? { ...item, status: "done" } : item,
          ),
        );
        notifyTaskDataChanged();
      }

      navigate(`/menu/sales/leads?lead_id=${task.lead_id}`);
    } catch (e) {
      console.error(e);
      setError(formatBackendError(e, t("error_updating_task"), t));
    } finally {
      setUpdatingId(null);
    }
  };

  const compactText = (value, max = 72) => {
    const clean = String(value || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!clean) return "-";
    if (clean.length <= max) return clean;

    return `${clean.slice(0, max).trim()}...`;
  };

  const extractTaskDetailValue = (description, label) => {
    const text = String(description || "");
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const labels = [
      "Telegram chat id",
      "Telegram username",
      "Phone",
      "Order description",
      "Delivery address",
      "Delivery date",
      "Delivery time",
    ];

    const otherLabels = labels
      .filter((item) => item !== label)
      .map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");

    const regex = new RegExp(
      `${escaped}:\\s*([\\s\\S]*?)(?=\\n(?:${otherLabels}):|$)`,
      "i",
    );

    const match = text.match(regex);
    return match?.[1]?.trim() || "";
  };

  const getTaskCompactDescription = (task) => {
    const description = task?.description || "";

    const orderDescription = extractTaskDetailValue(
      description,
      "Order description",
    );
    const deliveryAddress = extractTaskDetailValue(
      description,
      "Delivery address",
    );
    const deliveryDate = extractTaskDetailValue(description, "Delivery date");
    const deliveryTime = extractTaskDetailValue(description, "Delivery time");

    const parts = [
      orderDescription,
      deliveryAddress ? `Address: ${deliveryAddress}` : "",
      deliveryDate || deliveryTime
        ? `${deliveryDate} ${deliveryTime}`.trim()
        : "",
    ].filter(Boolean);

    if (parts.length) return compactText(parts.join(" · "), 86);

    return compactText(description, 86);
  };

  const getTaskFinance = (task) => {
    if (!task) return null;

    if (task.deal_id || task.deal_ui_id) {
      return {
        source: t("deal", { defaultValue: "Deal" }),
        sourceId: task.deal_ui_id ?? task.deal_id,
        amount: task.deal_amount,
        prepayment: task.deal_prepayment,
        balance: task.deal_balance,
        paymentStatus: task.deal_payment_status,
      };
    }

    if (task.lead_id || task.lead_ui_id) {
      return {
        source: t("sales_tasks_lead", { defaultValue: "Lead" }),
        sourceId: task.lead_ui_id ?? task.lead_id,
        amount: task.lead_budget,
        prepayment: task.lead_prepayment,
        balance: task.lead_balance,
        paymentStatus: task.lead_payment_status,
      };
    }

    return null;
  };

  const formatTaskFinanceAmount = (value) => {
    if (value == null || value === "") return "—";

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);

    const locale = i18n.resolvedLanguage === "en" ? "en-GB" : "ru-RU";
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);

    return `${formatted} €`;
  };

  const renderTaskFinancePanel = (task) => {
    const finance = getTaskFinance(task);
    if (
      !finance ||
      (finance.amount == null &&
        finance.prepayment == null &&
        finance.balance == null)
    ) {
      return null;
    }

    const rawPaymentStatus = String(
      finance.paymentStatus || "unpaid",
    ).toLowerCase();
    const paymentStatus = rawPaymentStatus === "paid"
      ? "paid"
      : rawPaymentStatus === "partial" ||
          rawPaymentStatus === "partially_paid" ||
          rawPaymentStatus === "partial_paid"
        ? "partial"
        : "unpaid";

    return (
      <section
        className="tasksDetailsFinance"
        aria-label={t("payment_summary", {
          defaultValue: "Payment summary",
        })}
      >
        <div className="tasksDetailsFinanceHeader">
          <div className="tasksDetailsFinanceHeading">
            <span className="tasksDetailsFinanceIcon" aria-hidden="true">
              €
            </span>
            <div className="tasksDetailsFinanceTitle">
              <strong>
                {t("payment_summary", { defaultValue: "Payment summary" })}
              </strong>
              <span>
                {t("payment_summary_hint", {
                  defaultValue: "Financial details of the linked CRM record",
                })}
              </span>
            </div>
          </div>
          <span className="tasksDetailsFinanceSource">
            {finance.source}
            {finance.sourceId != null ? ` #${finance.sourceId}` : ""}
          </span>
        </div>

        <div className="tasksDetailsFinanceGrid">
          <div className="tasksDetailsFinanceMetric is-amount">
            <span className="tasksDetailsFinanceMetricLabel">
              {t("payment_amount", { defaultValue: "Amount" })}
            </span>
            <strong className="tasksDetailsFinanceMetricValue">
              {formatTaskFinanceAmount(finance.amount)}
            </strong>
          </div>

          <div className="tasksDetailsFinanceMetric is-prepayment">
            <span className="tasksDetailsFinanceMetricLabel">
              {t("lead_prepayment", { defaultValue: "Prepayment" })}
            </span>
            <strong className="tasksDetailsFinanceMetricValue">
              {formatTaskFinanceAmount(finance.prepayment)}
            </strong>
          </div>

          <div className="tasksDetailsFinanceMetric is-balance">
            <span className="tasksDetailsFinanceMetricLabel">
              {t("lead_balance_due", { defaultValue: "Balance due" })}
            </span>
            <strong className="tasksDetailsFinanceMetricValue">
              {formatTaskFinanceAmount(finance.balance)}
            </strong>
          </div>

          <div className="tasksDetailsFinanceMetric is-status">
            <span className="tasksDetailsFinanceMetricLabel">
              {t("payment_status", { defaultValue: "Payment status" })}
            </span>
            <strong
              className={`tasksDetailsFinanceStatus is-${paymentStatus}`}
            >
              <span aria-hidden="true" />
              {t(`payment_status_${paymentStatus}`, {
                defaultValue: paymentStatus,
              })}
            </strong>
          </div>
        </div>
      </section>
    );
  };

  const getTaskDetailRows = (task) => {
    const description = task?.description || "";

    return [
      // {
      //   label: "Telegram chat id",
      //   value: extractTaskDetailValue(description, "Telegram chat id"),
      // },
      // {
      //   label: "Telegram username",
      //   value: extractTaskDetailValue(description, "Telegram username"),
      // },
      // {
      //   label: "Phone",
      //   value: extractTaskDetailValue(description, "Phone"),
      // },
      {
        label: t("task_detail_order_description"),
        value: extractTaskDetailValue(description, "Order description"),
      },
      {
        label: t("task_detail_delivery_address"),
        value: extractTaskDetailValue(description, "Delivery address"),
      },
      {
        label: t("task_detail_delivery_date"),
        value: extractTaskDetailValue(description, "Delivery date"),
      },
      {
        label: t("task_detail_delivery_time"),
        value: extractTaskDetailValue(description, "Delivery time"),
      },
    ].filter((row) => row.value);
  };

  const resetDescriptionEditor = () => {
    setDescriptionEditing(false);
    setDescriptionDraft("");
    setDescriptionError("");
    setDescriptionSaving(false);
  };

  const closeDescriptionFullscreen = () => {
    if (
      descriptionEditing &&
      descriptionDraft !== String(detailsTask?.description || "") &&
      !window.confirm(
        t("unsaved_changes_confirm", {
          defaultValue: "There are unsaved changes. Close without saving?",
        }),
      )
    ) {
      return;
    }

    setDescriptionFullscreen(false);
    resetDescriptionEditor();
  };

  const closeTaskDetails = () => {
    if (
      descriptionEditing &&
      descriptionDraft !== String(detailsTask?.description || "") &&
      !window.confirm(
        t("unsaved_changes_confirm", {
          defaultValue: "There are unsaved changes. Close without saving?",
        }),
      )
    ) {
      return;
    }

    setDescriptionFullscreen(false);
    resetDescriptionEditor();
    setDetailsTask(null);
    setDetailsTimeline([]);
  };

  const openTaskDetails = (task) => {
    setOpenMenuTaskId(null);
    setDescriptionFullscreen(false);
    resetDescriptionEditor();
    setDetailsTask(task);
    fetchTaskTimeline(task);
  };

  const startDescriptionEditing = () => {
    setDescriptionDraft(String(detailsTask?.description || ""));
    setDescriptionError("");
    setDescriptionEditing(true);
  };

  const cancelDescriptionEditing = () => {
    const original = String(detailsTask?.description || "");

    if (
      descriptionDraft !== original &&
      !window.confirm(
        t("discard_changes_confirm", {
          defaultValue: "Discard unsaved changes?",
        }),
      )
    ) {
      return;
    }

    setDescriptionDraft(original);
    setDescriptionEditing(false);
    setDescriptionError("");
  };

  const saveTaskDescription = async () => {
    if (!detailsTask?.id || descriptionSaving) return;

    try {
      setDescriptionSaving(true);
      setDescriptionError("");

      const response = await api.put(`/tasks/${detailsTask.id}`, {
        description: descriptionDraft.trim() || null,
      });

      const updatedTask = response.data || {
        ...detailsTask,
        description: descriptionDraft.trim() || null,
      };

      setDetailsTask((prev) => ({
        ...prev,
        ...updatedTask,
      }));

      setTasks((prev) =>
        prev.map((item) =>
          item.id === detailsTask.id ? { ...item, ...updatedTask } : item,
        ),
      );

      setTaskTabCache((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (Array.isArray(next[key])) {
            next[key] = next[key].map((item) =>
              item.id === detailsTask.id ? { ...item, ...updatedTask } : item,
            );
          }
        });
        return next;
      });

      setDescriptionDraft(String(updatedTask.description || ""));
      setDescriptionEditing(false);
      await fetchTaskTimeline(updatedTask);
    } catch (e) {
      console.error(e);
      setDescriptionError(
        formatBackendError(
          e,
          t("task_description_update_failed", {
            defaultValue: "Could not update task description",
          }),
          t,
        ),
      );
    } finally {
      setDescriptionSaving(false);
    }
  };

  const kebabBtnStyle = {
    width: 34,
    height: 24,
    padding: 0,
    fontSize: 16,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const menuStyle = {
    position: "fixed",
    top: menuPosition.top,
    left: menuPosition.left,
    minWidth: 240,
    zIndex: 99999,
    padding: 8,
    borderRadius: 12,
    background: "rgba(25, 25, 30, 0.98)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 18px 42px rgba(0,0,0,0.55)",
  };

  const itemStyle = (disabled) => ({
    width: "100%",
    textAlign: "left",
    padding: "9px 10px",
    borderRadius: 9,
    border: "none",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
  });

  const iconStyle = {
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 20px",
  };

  const MobileRelationLine = ({ label, children }) => (
    <span className="crmMobileRelationLine">
      <span className="crmMobileRelationLabel">{label}</span>
      <span className="crmMobileRelationValue">{children}</span>
    </span>
  );

  const MenuItem = ({
    title,
    disabled,
    icon,
    iconBg,
    iconBorder,
    academy,
    academyTraining,
    academyEntityTitle,
    onClick,
    children,
  }) => {
    return (
      <div title={title}>
        <button
          type="button"
          style={itemStyle(disabled)}
          aria-disabled={disabled ? "true" : "false"}
          data-academy={academy}
          data-academy-training-task={
            academyTraining ? "true" : undefined
          }
          data-academy-entity-title={academyEntityTitle || undefined}
          onClick={() => {
            if (disabled) return;
            onClick?.();
          }}
        >
          <span
            style={{
              ...iconStyle,
              background: iconBg,
              border: iconBorder,
            }}
            aria-hidden="true"
          >
            {icon}
          </span>
          <span style={{ flex: 1 }}>{children}</span>
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!descriptionFullscreen) return undefined;

    const handleDescriptionFullscreenKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDescriptionFullscreen();
      }

      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "s" &&
        descriptionEditing
      ) {
        event.preventDefault();
        saveTaskDescription();
      }
    };

    document.addEventListener("keydown", handleDescriptionFullscreenKeyDown);
    return () => {
      document.removeEventListener(
        "keydown",
        handleDescriptionFullscreenKeyDown,
      );
    };
  }, [
    descriptionFullscreen,
    descriptionEditing,
    descriptionDraft,
    descriptionSaving,
    detailsTask,
  ]);

  return (
    <div className="container mt-4 tasksPage">
      <div
        data-academy="tasks-header"
        className="tasksPageHeader d-flex justify-content-between align-items-center mb-3 gap-2 flex-nowrap"
      >
        <div className="tasksPageTitleBlock">
          <h2 className="mb-0">{t("sales_tasks")}</h2>
          <div className="tasksPageSubtitle">
            {t("sales_tasks_subtitle", {
              defaultValue: "Track and manage follow-up tasks.",
            })}
          </div>
        </div>

        <div className="d-flex gap-2 align-items-center flex-nowrap tasksPageHeaderActions">
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={loading}
          >
            {loading ? `${t("loading")}...` : t("refresh")}
          </button>
        </div>
      </div>

      <div
        data-academy="tasks-tabs"
        className="tasksTimeTabs"
        role="tablist"
        aria-label={t("task_time_groups", { defaultValue: "Task time groups" })}
      >
        {TASK_TIME_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            data-academy={`task-tab-${tab.key}`}
            role="tab"
            aria-selected={activeTaskTab === tab.key}
            className={`tasksTimeTab tasksTimeTab--${tab.key} ${
              activeTaskTab === tab.key ? "is-active" : ""
            }`}
            onClick={() => handleTaskTabChange(tab.key)}
            disabled={loading && activeTaskTab !== tab.key}
          >
            <span>{t(tab.label)}</span>
            <span className="tasksTimeTabCount">
              {taskTabCounts[tab.key] || 0}
            </span>
          </button>
        ))}
      </div>

      {academyOverdueActive && (
        <div
          data-academy="task-overdue-guide"
          className="tasksOverdueAcademyGuide"
          role="status"
        >
          <span className="tasksOverdueAcademyIcon" aria-hidden="true">
            !
          </span>
          <div>
            <strong>{t("tasks_academy_overdue_notice")}</strong>
            <span>
              {t("tasks_academy_overdue_hint")}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger py-2">
          {typeof error === "string" ? error : formatBackendError(error, "", t)}
        </div>
      )}

      {filtersError && (
        <div className="alert alert-warning py-2">{filtersError}</div>
      )}

      <div className="table-responsive tasksDesktopTable">
        <div
          data-academy="tasks-toolbar"
          className="tasksDesktopToolbar d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 px-2 pt-2"
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 90 }}
              value={tasksPerPage}
              onChange={handleTasksPerPageChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

            <div className="text-muted">{showingText}</div>
          </div>

          <div
            className="input-group input-group-sm"
            style={{ width: "auto", minWidth: 260 }}
          >
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading ? `${t("loading")}...` : t("saved_filters")}
              </option>
              {savedFilters.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.name}
                  {filter.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <table
          data-academy="tasks-table"
          className="table table-striped table-hover align-middle"
        >
          <thead>
            <tr>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("ui_id")}
              >
                {t("id")}
                {renderSortArrow("ui_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("title")}
              >
                {t("sales_tasks_title")}
                {renderSortArrow("title")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("description")}
              >
                {t("sales_tasks_description")}
                {renderSortArrow("description")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("due_at")}
              >
                {t("sales_tasks_due_at")}
                {renderSortArrow("due_at")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("status")}
              >
                {t("sales_tasks_status")}
                {renderSortArrow("status")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("owner_ui_id")}
              >
                {t("sales_tasks_owner")}
                {renderSortArrow("owner_ui_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("lead_ui_id")}
              >
                {t("sales_tasks_lead")}
                {renderSortArrow("lead_ui_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("deal_id")}
              >
                {t("sales_tasks_deal")}
                {renderSortArrow("deal_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("contact_ui_id")}
              >
                {t("sales_tasks_contact")}
                {renderSortArrow("contact_ui_id")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("created_at")}
              >
                {t("created_at")}
                {renderSortArrow("created_at")}
              </th>
              <th className="tasksStickyActions" style={{ width: 90 }}>
                {t("actions")}
              </th>
            </tr>

            <tr>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("id")}
                  value={columnFilters.ui_id}
                  onChange={(e) =>
                    handleColumnFilterChange("ui_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_title")}
                  value={columnFilters.title}
                  onChange={(e) =>
                    handleColumnFilterChange("title", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_description")}
                  value={columnFilters.description}
                  onChange={(e) =>
                    handleColumnFilterChange("description", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_due_at")}
                  value={columnFilters.due_at}
                  onChange={(e) =>
                    handleColumnFilterChange("due_at", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_status")}
                  value={columnFilters.status}
                  onChange={(e) =>
                    handleColumnFilterChange("status", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_owner")}
                  value={columnFilters.owner_ui_id}
                  onChange={(e) =>
                    handleColumnFilterChange("owner_ui_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_lead")}
                  value={columnFilters.lead_ui_id}
                  onChange={(e) =>
                    handleColumnFilterChange("lead_ui_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_deal")}
                  value={columnFilters.deal_id}
                  onChange={(e) =>
                    handleColumnFilterChange("deal_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("sales_tasks_contact")}
                  value={columnFilters.contact_ui_id}
                  onChange={(e) =>
                    handleColumnFilterChange("contact_ui_id", e.target.value)
                  }
                />
              </th>
              <th>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t("created_at")}
                  value={columnFilters.created_at}
                  onChange={(e) =>
                    handleColumnFilterChange("created_at", e.target.value)
                  }
                />
              </th>
              <th className="tasksStickyActions" />
            </tr>
          </thead>

          <tbody>
            {visibleTasks.length === 0 && !loading && (
              <tr>
                <td colSpan={11} className="text-center py-4">
                  {t("sales_tasks_empty")}
                </td>
              </tr>
            )}

            {visibleTasks.map((task) => (
              <tr
                key={task.id}
                data-academy="task-row"
                data-academy-training-task={
                  isAcademyTrainingTask(task) ? "true" : undefined
                }
                data-academy-entity-title={task.title || ""}
                className={
                  isAcademyTrainingTask(task)
                    ? "tasksAcademyTrainingRow"
                    : undefined
                }
              >
                <td>{task.ui_id ?? "-"}</td>
                <td>{task.title}</td>
                <td>
                  <div className="tasksCompactDescription">
                    <span>{getTaskCompactDescription(task)}</span>
                    {task.description && (
                      <button
                        type="button"
                        className="tasksDetailsLink"
                        onClick={() => openTaskDetails(task)}
                      >
                        {t("details", { defaultValue: "Details" })}
                      </button>
                    )}
                  </div>
                </td>
                <td
                  data-academy="task-due"
                  data-academy-training-task={
                    isAcademyTrainingTask(task) ? "true" : undefined
                  }
                >
                  {task.due_at ? formatTenantLocalDateTime(task.due_at) : "-"}
                </td>
                <td>
                  <span className={getStatusBadgeClass(task.status)}>
                    {getStatusLabel(task.status)}
                  </span>
                </td>
                <td>{renderOwnerCell(task)}</td>
                <td>{renderLeadCell(task)}</td>
                <td>{renderDealCell(task)}</td>
                <td>{renderContactCell(task)}</td>
                <td>
                  {task.created_at
                    ? new Date(task.created_at).toLocaleString()
                    : "-"}
                </td>
                <td className="tasksStickyActions">
                  <span title={t("actions")}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      style={kebabBtnStyle}
                      data-academy="task-actions-trigger"
                      data-academy-training-task={
                        isAcademyTrainingTask(task) ? "true" : undefined
                      }
                      data-academy-entity-title={task.title || ""}
                      onClick={(e) => openActionsMenu(task.id, e)}
                      aria-expanded={
                        openMenuTaskId === task.id ? "true" : "false"
                      }
                      aria-haspopup="menu"
                    >
                      ⋯
                    </button>
                  </span>

                  {openMenuTaskId === task.id &&
                    createPortal(
                      <div
                        ref={openMenuRef}
                        role="menu"
                        aria-label={t("tasks_actions_aria")}
                        data-academy="task-actions-menu"
                        data-academy-entity-title={task.title || ""}
                        style={menuStyle}
                      >
                        <MenuItem
                          title={t("details", { defaultValue: "Details" })}
                          disabled={false}
                          icon="i"
                          iconBg="rgba(13,110,253,0.20)"
                          iconBorder="1px solid rgba(13,110,253,0.35)"
                          onClick={() => openTaskDetails(task)}
                        >
                          {t("details", { defaultValue: "Details" })}
                        </MenuItem>

                        <MenuItem
                          title={t("task_done_and_open_lead", {
                            defaultValue: "Done and open lead",
                          })}
                          disabled={
                            !task.lead_id ||
                            updatingId === task.id ||
                            deletingId === task.id
                          }
                          icon="→"
                          iconBg="rgba(25,135,84,0.25)"
                          iconBorder="1px solid rgba(25,135,84,0.35)"
                          onClick={() => completeTaskAndOpenLead(task)}
                        >
                          {updatingId === task.id
                            ? t("updating")
                            : t("task_done_and_open_lead", {
                                defaultValue: "Done and open lead",
                              })}
                        </MenuItem>

                        <MenuItem
                          title={t("task_mark_as_done")}
                          disabled={
                            isDoneDisabled(task) || deletingId === task.id
                          }
                          icon="✓"
                          iconBg="rgba(25,135,84,0.25)"
                          iconBorder="1px solid rgba(25,135,84,0.35)"
                          academy="task-mark-done"
                          academyTraining={isAcademyTrainingTask(task)}
                          academyEntityTitle={task.title}
                          onClick={() => updateTaskStatus(task, "done")}
                        >
                          {updatingId === task.id && task.status !== "done"
                            ? t("updating")
                            : t("task_mark_as_done")}
                        </MenuItem>

                        <MenuItem
                          title={t("task_back_to_pending")}
                          disabled={
                            isBackToPendingDisabled(task) ||
                            deletingId === task.id
                          }
                          icon="↺"
                          iconBg="rgba(108,117,125,0.20)"
                          iconBorder="1px solid rgba(108,117,125,0.35)"
                          onClick={() => updateTaskStatus(task, "pending")}
                        >
                          {updatingId === task.id && task.status !== "pending"
                            ? t("updating")
                            : t("task_back_to_pending")}
                        </MenuItem>

                        {getUserPermissions()?.includes("tasks.delete") && (
                          <>
                            <div
                              style={{
                                height: 1,
                                margin: "6px 2px",
                                background: "rgba(255,255,255,0.10)",
                              }}
                            />

                            <MenuItem
                              title={t("delete", { defaultValue: "Delete" })}
                              disabled={
                                deletingId === task.id || updatingId === task.id
                              }
                              icon={<i className="lni lni-trash-3"></i>}
                              iconBg="rgba(220,53,69,0.20)"
                              iconBorder="1px solid rgba(220,53,69,0.35)"
                              onClick={() => handleDeleteTask(task)}
                            >
                              {deletingId === task.id
                                ? t("deleting", { defaultValue: "Deleting..." })
                                : t("delete", { defaultValue: "Delete" })}
                            </MenuItem>
                          </>
                        )}
                      </div>,
                      document.body,
                    )}
                </td>
              </tr>
            ))}

            {loading && (
              <tr>
                <td colSpan={11} className="text-center py-4">
                  {t("loading")}...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="tasksMobileTableCard">
        <div className="tasksMobileTableToolbar">
          <div className="tasksMobileToolbarTitle">
            {t("table_filters", { defaultValue: "Table filters" })}
          </div>

          <div className="tasksMobileToolbarActions">
            <select
              className="form-select"
              value={selectedFilterId || ""}
              onChange={handleSelectSavedFilter}
              disabled={filtersLoading}
            >
              <option value="">
                {filtersLoading ? `${t("loading")}...` : t("saved_filters")}
              </option>
              {savedFilters.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.name}
                  {filter.is_default ? " ⭐" : ""}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-success"
              type="button"
              title={t("save_filter")}
              onClick={handleSaveCurrentAsNew}
              disabled={savingFilter}
            >
              <i className="lni lni-floppy-disk-1"></i>
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              title={t("update_filter")}
              onClick={handleUpdateSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-refresh-circle-1-clockwise"></i>
            </button>

            <button
              className="btn btn-outline-danger"
              type="button"
              title={t("delete_filter")}
              onClick={handleDeleteSelectedFilter}
              disabled={savingFilter || !selectedFilterId}
            >
              <i className="lni lni-trash-3"></i>
            </button>
          </div>
        </div>

        <div className="tasksMobileTableMeta">
          <select
            className="form-select"
            value={tasksPerPage}
            onChange={handleTasksPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <div>{showingText}</div>
        </div>

        <div className="tasksMobileTableSearch">
          <input
            type="text"
            className="form-control"
            placeholder={t("search", { defaultValue: "Search" })}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="tasksMobileTableScroll">
          <table className="tasksMobileTable">
            <thead>
              <tr>
                <th>{t("id")}</th>
                <th>{t("sales_tasks_title")}</th>
              </tr>

              <tr>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("id")}
                    value={columnFilters.ui_id}
                    onChange={(e) =>
                      handleColumnFilterChange("ui_id", e.target.value)
                    }
                  />
                </th>

                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={t("sales_tasks_title")}
                    value={columnFilters.title}
                    onChange={(e) =>
                      handleColumnFilterChange("title", e.target.value)
                    }
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleTasks.length === 0 && !loading && (
                <tr>
                  <td colSpan={2} className="tasksMobileEmptyCell">
                    {t("sales_tasks_empty")}
                  </td>
                </tr>
              )}

              {visibleTasks.map((task) => (
                <tr
                  key={task.id}
                  data-academy="task-row"
                  data-academy-training-task={
                    isAcademyTrainingTask(task) ? "true" : undefined
                  }
                  data-academy-entity-title={task.title || ""}
                  className={
                    isAcademyTrainingTask(task)
                      ? "tasksAcademyTrainingRow"
                      : undefined
                  }
                >
                  <td className="tasksMobileIdCell">{task.ui_id ?? "-"}</td>

                  <td>
                    <div className="tasksMobileTaskCell">
                      <div className="tasksMobileTaskTop">
                        <strong>{task.title}</strong>

                        <span
                          className={`${getStatusBadgeClass(task.status)} tasksMobileStatus`}
                        >
                          {getStatusLabel(task.status)}
                        </span>
                      </div>

                      <div className="tasksMobileTaskMeta">
                        <span>
                          {t("sales_tasks_description")}:{" "}
                          <b>{getTaskCompactDescription(task)}</b>
                          {task.description && (
                            <button
                              type="button"
                              className="tasksMobileDetailsLink"
                              onClick={() => openTaskDetails(task)}
                            >
                              {t("details", { defaultValue: "Details" })}
                            </button>
                          )}
                        </span>

                        <span>
                          {t("sales_tasks_due_at")}:{" "}
                          <b
                            data-academy="task-due"
                            data-academy-training-task={
                              isAcademyTrainingTask(task) ? "true" : undefined
                            }
                          >
                            {task.due_at
                              ? formatTenantLocalDateTime(task.due_at)
                              : "-"}
                          </b>
                        </span>

                        <span>
                          {t("sales_tasks_owner")}:{" "}
                          <b>{renderOwnerCell(task)}</b>
                        </span>

                        <span>
                          {t("sales_tasks_lead")}: <b>{renderLeadCell(task)}</b>
                        </span>

                        <span>
                          {t("sales_tasks_deal")}:{" "}
                          <b>{task.deal_ui_id ?? task.deal_id ?? "-"}</b>
                        </span>

                        <span>
                          {t("sales_tasks_contact")}:{" "}
                          <b>{renderContactCell(task)}</b>
                        </span>

                        <span>
                          {t("created_at")}:{" "}
                          <b>
                            {task.created_at
                              ? new Date(task.created_at).toLocaleString()
                              : "-"}
                          </b>
                        </span>
                      </div>

                      <div className="tasksMobileTaskActions">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          type="button"
                          onClick={() => openTaskDetails(task)}
                        >
                          {t("details", { defaultValue: "Details" })}
                        </button>

                        <button
                          className="btn btn-primary btn-sm"
                          type="button"
                          disabled={
                            !task.lead_id ||
                            updatingId === task.id ||
                            deletingId === task.id
                          }
                          onClick={() => completeTaskAndOpenLead(task)}
                        >
                          {updatingId === task.id
                            ? t("updating")
                            : t("task_done_and_open_lead", {
                                defaultValue: "Done and open lead",
                              })}
                        </button>

                        <button
                          className="btn btn-success btn-sm"
                          data-academy="task-mark-done"
                          data-academy-training-task={
                            isAcademyTrainingTask(task) ? "true" : undefined
                          }
                          data-academy-entity-title={task.title || ""}
                          disabled={
                            isDoneDisabled(task) || deletingId === task.id
                          }
                          onClick={() => updateTaskStatus(task, "done")}
                        >
                          {updatingId === task.id && task.status !== "done"
                            ? t("updating")
                            : t("task_mark_as_done")}
                        </button>

                        <button
                          className="btn btn-outline-secondary btn-sm"
                          disabled={
                            isBackToPendingDisabled(task) ||
                            deletingId === task.id
                          }
                          onClick={() => updateTaskStatus(task, "pending")}
                        >
                          {updatingId === task.id && task.status !== "pending"
                            ? t("updating")
                            : t("task_back_to_pending")}
                        </button>

                        {getUserPermissions()?.includes("tasks.delete") && (
                          <button
                            className="btn btn-outline-danger btn-sm tasksMobileDeleteBtn"
                            type="button"
                            disabled={
                              deletingId === task.id || updatingId === task.id
                            }
                            onClick={() => handleDeleteTask(task)}
                          >
                            {deletingId === task.id
                              ? t("deleting", { defaultValue: "Deleting..." })
                              : t("delete", { defaultValue: "Delete" })}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={2} className="tasksMobileEmptyCell">
                    {t("loading")}...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2 tasksPaginationWrap">
        <nav aria-label={t("tasks_pagination_aria")}>
          <ul className="pagination justify-content-end mb-0 tasksPagination tasksCompactPagination">
            <li
              className={`page-item ${safePage === 1 || totalTasks === 0 ? "disabled" : ""}`}
            >
              <button
                className="page-link"
                title={t("first", { defaultValue: "First" })}
                aria-label={t("first", { defaultValue: "First" })}
                onClick={() => paginate(1)}
              >
                «
              </button>
            </li>

            <li
              className={`page-item ${
                safePage === 1 || totalTasks === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("previous")}
                aria-label={t("previous")}
                onClick={() => paginate(safePage - 1)}
              >
                {t("previous")}
              </button>
            </li>

            {getCompactPaginationItems(safePage, totalPages).map((item) => {
              if (typeof item === "string") {
                return (
                  <li
                    key={item}
                    className="page-item disabled tasksPaginationEllipsis"
                  >
                    <span className="page-link">…</span>
                  </li>
                );
              }

              return (
                <li
                  key={item}
                  className={`page-item ${safePage === item ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => paginate(item)}>
                    {item}
                  </button>
                </li>
              );
            })}

            <li
              className={`page-item ${
                safePage === totalPages || totalTasks === 0 ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                title={t("next")}
                aria-label={t("next")}
                onClick={() => paginate(safePage + 1)}
              >
                {t("next")}
              </button>
            </li>

            <li
              className={`page-item ${safePage === totalPages || totalTasks === 0 ? "disabled" : ""}`}
            >
              <button
                className="page-link"
                title={t("last", { defaultValue: "Last" })}
                aria-label={t("last", { defaultValue: "Last" })}
                onClick={() => paginate(totalPages)}
              >
                »
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {detailsTask && (
        <div
          className="modal fade show tasksDetailsModalBackdrop"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div
            className="modal-dialog modal-xl tasksDetailsDialog"
            role="document"
          >
            <div className="modal-content tasksDetailsModalContent">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">
                    {t("details", { defaultValue: "Details" })}
                  </h5>
                  <div className="tasksDetailsModalSubtitle">
                    #{detailsTask.ui_id ?? detailsTask.id} · {detailsTask.title}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeTaskDetails}
                />
              </div>

              <div className="modal-body">
                <div className="tasksDetailsGrid">
                  <div className="tasksDetailsItem">
                    <span>{t("sales_tasks_status")}</span>
                    <b>{getStatusLabel(detailsTask.status)}</b>
                  </div>

                  <div className="tasksDetailsItem">
                    <span>{t("sales_tasks_due_at")}</span>
                    <b>
                      {detailsTask.due_at
                        ? formatTenantLocalDateTime(detailsTask.due_at)
                        : "-"}
                    </b>
                  </div>

                  <div className="tasksDetailsItem">
                    <span>{t("sales_tasks_contact")}</span>
                    <b>{renderContactCell(detailsTask)}</b>
                  </div>

                  <div className="tasksDetailsItem">
                    <span>{t("sales_tasks_lead")}</span>
                    <b>{renderLeadCell(detailsTask)}</b>
                  </div>

                  <div className="tasksDetailsItem">
                    <span>{t("deal", { defaultValue: "Deal" })}</span>
                    <b>{renderDealCell(detailsTask)}</b>
                  </div>
                </div>

                {renderTaskFinancePanel(detailsTask)}

                {getTaskDetailRows(detailsTask).length > 0 && (
                  <div className="tasksDetailsParsed">
                    {getTaskDetailRows(detailsTask).map((row) => (
                      <div className="tasksDetailsParsedRow" key={row.label}>
                        <span>{row.label}</span>
                        <b>{row.value}</b>
                      </div>
                    ))}
                  </div>
                )}

                <div className="tasksDetailsFullText tasksDetailsFullTextCompact">
                  <div className="tasksDetailsFullTextHeader">
                    <div className="tasksDetailsFullTextTitle">
                      {t("sales_tasks_description")}
                    </div>

                    <div className="tasksDescriptionActions">
                      {!descriptionEditing ? (
                        <button
                          type="button"
                          className="tasksDescriptionIconButton"
                          onClick={startDescriptionEditing}
                          title={t("edit", { defaultValue: "Edit" })}
                          aria-label={t("edit", { defaultValue: "Edit" })}
                        >
                          ✎
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="tasksDescriptionIconButton tasksDescriptionIconButton--save"
                            onClick={saveTaskDescription}
                            disabled={descriptionSaving}
                            title={t("save", { defaultValue: "Save" })}
                            aria-label={t("save", { defaultValue: "Save" })}
                          >
                            {descriptionSaving ? "…" : "✓"}
                          </button>

                          <button
                            type="button"
                            className="tasksDescriptionIconButton tasksDescriptionIconButton--cancel"
                            onClick={cancelDescriptionEditing}
                            disabled={descriptionSaving}
                            title={t("cancel", { defaultValue: "Cancel" })}
                            aria-label={t("cancel", { defaultValue: "Cancel" })}
                          >
                            ×
                          </button>
                        </>
                      )}

                      <button
                        type="button"
                        className="tasksDescriptionIconButton"
                        onClick={() => setDescriptionFullscreen(true)}
                        title={t("expand", { defaultValue: "Expand" })}
                        aria-label={t("expand", { defaultValue: "Expand" })}
                      >
                        ⛶
                      </button>
                    </div>
                  </div>

                  {descriptionEditing ? (
                    <textarea
                      className="tasksDescriptionTextarea"
                      value={descriptionDraft}
                      onChange={(event) =>
                        setDescriptionDraft(event.target.value)
                      }
                      disabled={descriptionSaving}
                      autoFocus
                    />
                  ) : (
                    <pre>{detailsTask.description || "-"}</pre>
                  )}

                  {descriptionError && (
                    <div className="tasksDescriptionError">
                      {descriptionError}
                    </div>
                  )}
                </div>

                {descriptionFullscreen && (
                  <div
                    className="tasksDescriptionFullscreenBackdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("sales_tasks_description")}
                    onMouseDown={(event) => {
                      if (event.target === event.currentTarget) {
                        closeDescriptionFullscreen();
                      }
                    }}
                  >
                    <div className="tasksDescriptionFullscreenPanel">
                      <div className="tasksDescriptionFullscreenHeader">
                        <div>
                          <h4>{t("sales_tasks_description")}</h4>
                          <span>
                            #{detailsTask.ui_id ?? detailsTask.id} ·{" "}
                            {detailsTask.title}
                          </span>
                        </div>

                        <div className="tasksDescriptionActions">
                          {!descriptionEditing ? (
                            <button
                              type="button"
                              className="tasksDescriptionIconButton tasksDescriptionIconButton--large"
                              onClick={startDescriptionEditing}
                              title={t("edit", { defaultValue: "Edit" })}
                              aria-label={t("edit", { defaultValue: "Edit" })}
                            >
                              ✎
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="tasksDescriptionIconButton tasksDescriptionIconButton--large tasksDescriptionIconButton--save"
                                onClick={saveTaskDescription}
                                disabled={descriptionSaving}
                                title={t("save", { defaultValue: "Save" })}
                                aria-label={t("save", { defaultValue: "Save" })}
                              >
                                {descriptionSaving ? "…" : "✓"}
                              </button>

                              <button
                                type="button"
                                className="tasksDescriptionIconButton tasksDescriptionIconButton--large tasksDescriptionIconButton--cancel"
                                onClick={cancelDescriptionEditing}
                                disabled={descriptionSaving}
                                title={t("cancel", { defaultValue: "Cancel" })}
                                aria-label={t("cancel", {
                                  defaultValue: "Cancel",
                                })}
                              >
                                ×
                              </button>
                            </>
                          )}

                          <button
                            type="button"
                            className="tasksDescriptionIconButton tasksDescriptionIconButton--large"
                            onClick={closeDescriptionFullscreen}
                            title={t("close", { defaultValue: "Close" })}
                            aria-label={t("close", { defaultValue: "Close" })}
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      <div className="tasksDescriptionFullscreenBody">
                        {descriptionEditing ? (
                          <textarea
                            className="tasksDescriptionTextarea tasksDescriptionTextarea--fullscreen"
                            value={descriptionDraft}
                            onChange={(event) =>
                              setDescriptionDraft(event.target.value)
                            }
                            disabled={descriptionSaving}
                            autoFocus
                          />
                        ) : (
                          <pre>{detailsTask.description || "-"}</pre>
                        )}

                        {descriptionError && (
                          <div className="tasksDescriptionError">
                            {descriptionError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <TaskPhotosGallery
                  entityType="task"
                  entityId={detailsTask.id}
                  uploadUrl={`/tasks/${detailsTask.id}/photos`}
                  title={t("task_photos_title", {
                    defaultValue: "Task photos",
                  })}
                  hint={t("task_photos_hint", {
                    defaultValue:
                      "Photos are saved to the client Google Drive and appear in the timeline.",
                  })}
                  addLabel={t("add_photos", { defaultValue: "Add photos" })}
                  onChanged={() => fetchTaskTimeline(detailsTask)}
                />

                <CrmJourneyTimeline
                  target={
                    detailsTask?.ui_id || detailsTask?.id
                      ? {
                          type: "task",
                          uiId: detailsTask.ui_id || detailsTask.id,
                        }
                      : null
                  }
                  title={t("timeline", { defaultValue: "Timeline" })}
                  scopeLabel={t("crm_activities_timeline_for_task", {
                    defaultValue: "Task timeline",
                  })}
                  limit={50}
                  events={detailsTimelineLoading ? undefined : detailsTimeline}
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeTaskDetails}
                >
                  {t("close", { defaultValue: "Close" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {createModalOpen && (
        <div
          data-academy="task-create-modal"
          className="modal fade show tasksModalBackdrop"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("create_task")}
                  {createContacts.length > 0
                    ? ` (${createContacts.length})`
                    : ""}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeCreateModal}
                  disabled={createSaving}
                />
              </div>

              <div className="modal-body">
                {createError && (
                  <div className="alert alert-danger py-1 mb-2">
                    {typeof createError === "string"
                      ? createError
                      : formatBackendError(createError, "", t)}
                  </div>
                )}

                {createUIContacts.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">{t("sales_contacts")}</label>
                    <div
                      className="form-control"
                      style={{ maxHeight: 120, overflowY: "auto" }}
                    >
                      {createUIContacts.join(", ")}
                    </div>
                    <small className="text-muted">
                      {t("tasks_modal_contacts_hint")}
                    </small>
                  </div>
                )}

                {(createLeadId || createDealId) && (
                  <div className="mb-3">
                    <label className="form-label">{t("tasks_linked_to")}</label>
                    <div className="form-control">
                      {createLeadId && (
                        <span className="me-2">
                          {`${t("lead")}: ${createLeadUiId ?? createLeadId}`}
                        </span>
                      )}
                      {createDealId && (
                        <span>{`${t("deal")}: ${createDealId}`}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_tasks_title")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    data-academy="task-title"
                    type="text"
                    className="form-control"
                    value={createForm.title}
                    disabled={createSaving}
                    onChange={(e) =>
                      handleCreateChange("title", e.target.value)
                    }
                    placeholder={t("tasks_create_title_placeholder")}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_tasks_description")}
                  </label>
                  <textarea
                    data-academy="task-description"
                    className="form-control"
                    rows={3}
                    value={createForm.description}
                    disabled={createSaving}
                    onChange={(e) =>
                      handleCreateChange("description", e.target.value)
                    }
                    placeholder={t("task_description_optional")}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    {t("sales_tasks_due_at")}
                  </label>
                  <input
                    data-academy="task-due-at"
                    type="datetime-local"
                    className="form-control"
                    value={createForm.due_at}
                    disabled={createSaving}
                    onChange={(e) =>
                      handleCreateChange("due_at", e.target.value)
                    }
                  />
                  <small className="text-muted">
                    {t("task_due_optional_hint")}
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeCreateModal}
                  disabled={createSaving}
                >
                  {t("cancel")}
                </button>
                <button
                  data-academy="task-create-submit"
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateSubmit}
                  disabled={createSaving}
                >
                  {createSaving ? t("saving") : t("create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
